import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Trash2, Upload } from 'lucide-react';
import { organizationAPI, ManualInfo } from '../services/api';
import { SheetSection } from './settings/sheet';
import { DANGER_GHOST_BTN, GHOST_BTN, KeyValue, Notice, PRIMARY_BTN } from './shared/listPage';
import { fmtDateTime } from './org/orgFormat';

const MAX_MB = 20; // ίδιο με το backend (utils/orgManual.js) και τον nginx του Κουλουράδες

/**
 * Κεντρικά → Ρυθμίσεις → «Εγχειρίδιο χρήσης» — από 14/10/2026 ενότητα του «Φύλλου» (SheetSection) αντί
 * για κουτί με εικονίδιο. Το PDF που ανεβαίνει εδώ το κατεβάζουν όλα τα καταστήματα του οργανισμού από
 * το μενού πάνω δεξιά· κάθε νέο ανέβασμα γίνεται αμέσως η έκδοση που παίρνουν. Ίδιες κλήσεις, ίδιοι
 * έλεγχοι (μόνο PDF, ως 20 MB, 413 = ο server δεν το δέχεται), ίδια αφαίρεση σε δύο πατήματα.
 */
const OrgManualSection: React.FC<{ orgId: number }> = ({ orgId }) => {
  const { t } = useTranslation(['orgDashboard', 'common']);
  const [manual, setManual] = useState<ManualInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'upload' | 'download' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [armedDelete, setArmedDelete] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    organizationAPI.getManual(orgId)
      .then(r => { if (alive) setManual(r.manual); })
      .catch(() => { if (alive) setError(t('orgDashboard:manual.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- μία φόρτωση ανά οργανισμό
  }, [orgId]);

  useEffect(() => {
    if (!armedDelete) return undefined;
    const timer = setTimeout(() => setArmedDelete(false), 4000);
    return () => clearTimeout(timer);
  }, [armedDelete]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setError(t('orgDashboard:manual.notPdf'));
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(t('orgDashboard:manual.tooBig', { size: (file.size / 1048576).toFixed(1), max: MAX_MB }));
      return;
    }
    setBusy('upload');
    setError(null);
    try {
      const r = await organizationAPI.uploadManual(orgId, file);
      setManual(r.manual);
    } catch (err: any) {
      setError(err.response?.status === 413
        ? t('orgDashboard:manual.serverTooBig')
        : (err.response?.data?.error || t('orgDashboard:manual.uploadFailed')));
    } finally {
      setBusy(null);
    }
  };

  const onDownload = async () => {
    if (!manual) return;
    setBusy('download');
    setError(null);
    try {
      await organizationAPI.downloadManual(orgId, manual.file_name);
    } catch {
      setError(t('orgDashboard:manual.downloadFailed'));
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    if (!armedDelete) { setArmedDelete(true); return; }
    setArmedDelete(false);
    setBusy('delete');
    setError(null);
    try {
      await organizationAPI.deleteManual(orgId);
      setManual(null);
    } catch (err: any) {
      setError(err.response?.data?.error || t('orgDashboard:manual.deleteFailed'));
    } finally {
      setBusy(null);
    }
  };

  const uploadButton = (label: string, primary: boolean) => (
    <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy} className={primary ? PRIMARY_BTN : GHOST_BTN} data-testid="org-manual-upload">
      {busy === 'upload' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Upload className="w-4 h-4" aria-hidden="true" />}
      {busy === 'upload' ? t('orgDashboard:manual.uploading') : label}
    </button>
  );

  return (
    <SheetSection id="org-manual" title={t('orgDashboard:manual.title')} hint={t('orgDashboard:manual.hint')} testId="org-manual">
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="org-manual-file" />

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-t3" role="status">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('common:loading')}
        </p>
      ) : manual ? (
        <>
          <KeyValue
            testId="org-manual-info"
            items={[
              { id: 'file', label: t('orgDashboard:manual.file'), value: <span className="break-all">{manual.file_name}</span> },
              { id: 'uploaded', label: t('orgDashboard:manual.uploaded'), value: fmtDateTime(manual.uploaded_at, true), hint: manual.uploaded_by || undefined },
              { id: 'size', label: t('orgDashboard:manual.size'), value: t('orgDashboard:manual.megabytes', { size: (manual.size_bytes / 1048576).toFixed(1) }) },
            ]}
          />
          <div className="flex flex-wrap gap-2 mt-5">
            {uploadButton(t('orgDashboard:manual.newVersion'), false)}
            <button type="button" onClick={onDownload} disabled={!!busy} className={GHOST_BTN} data-testid="org-manual-download">
              {busy === 'download' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
              {t('orgDashboard:manual.download')}
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={!!busy}
              className={`${DANGER_GHOST_BTN} ${armedDelete ? 'bg-red-500/10 font-semibold' : ''}`}
              aria-live="polite"
              data-testid="org-manual-delete"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
              {armedDelete ? t('orgDashboard:manual.confirmDelete') : t('orgDashboard:manual.remove')}
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-t3" data-testid="org-manual-empty">{t('orgDashboard:manual.empty')}</p>
          {uploadButton(t('orgDashboard:manual.uploadPdf'), true)}
        </div>
      )}

      {error && <div className="mt-4"><Notice tone="danger" testId="org-manual-error">{error}</Notice></div>}
    </SheetSection>
  );
};

export default OrgManualSection;
